<script setup lang="ts">
import axios from 'axios'
import dayjs from 'dayjs'
import { useInfiniteScroll } from '@vueuse/core'
import { Download } from 'lucide-vue-next'
import { useRoute } from 'vue-router'

import SubscriptionItems from '@/views/settings/billing/Components/SubscriptionItems.vue'

import type { ChipColor } from '@/components/base/BaseChip.vue'
import type { Icons } from '@/components/base/BaseIcon.vue'

interface Client {
  public_id: string
  name: string
  registration_number: string
  payment_method: {
    slug: string
    name: string
  }
  client_mandates: {
    isDefault: number
    mandate?: {
      name: string
    }
  }[]
  pennylane?: {
    billing_address?: {
      address: string
      postal_code: string
      city: string
    }
  }
  distributions?: {
    store_company_product: {
      company_product: {
        slug: string
        is_subscription: boolean
      }
      store: {
        name: string
        public_id: string
      }
      sms_pack: {
        included_quantity: number
      } | null
      started_at_formatted: string
      stopped_at_formatted: string | null
      discounted_price_without_vat: number
    }
  }[]
}

interface Invoice {
  id: string
  invoice_number: string
  date: string
  currency_amount_before_tax: number
  currency_tax: number
  currency_amount: number
  public_file_url: string | null
  status: string
  type?: string
  label?: string
  invoice_lines: {
    product_id?: string
    unit?: string
  }[]
}

interface Tab {
  id: string
  name: string
  icon: Icons
}

interface TableRow {
  id: string
  data: {
    paymentReference: string
    label?: string
    date: string
    paidMonth: {
      label: string
    }
    totalPriceWithoutVat: string
    totalVat: string
    totalPriceVatIncluded: string
    type: {
      label: string
      color: ChipColor
    }
    status: {
      label: string
      color: ChipColor
    }
    download: {
      icon?: any
      color?: string
    }
  }
}

interface Distribution {
  product_name: string
  start_date: string
  end_date: string | null
  price_ht: number
  client: string
  isSubscription: boolean
  isSmsPack: boolean
  smsIncluded: number | null
  stores: {
    store_name: string
    store_id: string
  }[]
}

const { t } = useI18n()
const notification = useNotification()
const { formatNumber } = useNumber()
const { user } = useLocalStorage()
const { openInNewTab } = useDOM()
const route = useRoute()

const requestedTab = route.query.activeTab
const currentTab = ref(requestedTab === 'billing' || requestedTab === 'payment-methods' ? requestedTab : 'invoices')
const isLoaded = ref(false)
const isInvoicesLoaded = ref(false)
const isLoadingMore = ref(false)
const selectedClient = ref<string | null>(null)
const invoices = ref<Invoice[]>([])
const clients = ref<Client[]>([])
const nextCursor = ref<string | null>(null)
const hasMore = ref(false)
const table = ref({
  columns: {
    paymentReference: {
      id: 'paymentReference',
      label: 'pages.settings.accountPage.tables.thead.paymentReference'
    },
    label: {
      id: 'label',
      label: 'pages.settings.accountPage.tables.thead.label'
    },
    date: {
      id: 'date',
      label: 'pages.settings.accountPage.tables.thead.date'
    },
    paidMonth: {
      id: 'paidMonth',
      label: 'pages.settings.accountPage.tables.thead.paidMonth'
    },
    totalPriceWithoutVat: {
      id: 'totalPriceWithoutVat',
      label: 'pages.settings.accountPage.tables.thead.totalPriceWithoutVat',
      numeric: true
    },
    totalVat: {
      id: 'totalVat',
      label: 'pages.settings.accountPage.tables.thead.totalVat',
      numeric: true
    },
    totalPriceVatIncluded: {
      id: 'totalPriceVatIncluded',
      label: 'pages.settings.accountPage.tables.thead.totalPriceVatIncluded',
      numeric: true
    },
    type: {
      id: 'type',
      label: 'pages.settings.accountPage.tables.thead.type'
    },
    status: {
      id: 'status',
      label: 'pages.settings.accountPage.tables.thead.status'
    },
    download: {
      id: 'download',
      label: 'pages.settings.accountPage.tables.thead.download',
      sortable: false,
      pinned: 'right' as const,
      pinnedWidth: 100
    }
  },
  rows: [] as TableRow[],
  sort: undefined,
  pagination: null
})

const translatedColumns = computed(() =>
  Object.fromEntries(
    Object.entries(table.value.columns).map(([key, col]) => [key, { ...col, label: t((col as any).label) }])
  )
)

const pageTitle = ref({
  title: 'pages.settings.accountPage.title',
  description: 'pages.settings.accountPage.description'
})

const tabs = computed<Tab[]>(() => [
  { id: 'invoices', name: t('pages.settings.accountPage.tabs.invoices'), icon: 'HeroDocumentTextIconSolid' },
  { id: 'billing', name: t('pages.settings.accountPage.tabs.billing'), icon: 'HeroBoltIconSolid' },
  {
    id: 'payment-methods',
    name: t('pages.settings.accountPage.tabs.payment-methods'),
    icon: 'HeroCreditCardIconSolid'
  }
])

const distributions = computed<Distribution[]>(() => {
  const result: Distribution[] = []

  clients.value.forEach((client) => {
    const clientName = client.name

    if (client.distributions && client.distributions.length > 0) {
      client.distributions.forEach((distribution) => {
        if (!distribution.store_company_product) {
          return
        }

        const storeCompanyProduct = distribution.store_company_product
        const product = storeCompanyProduct.company_product
        const store = storeCompanyProduct.store
        const smsPack = storeCompanyProduct.sms_pack
        const endDate = storeCompanyProduct.stopped_at_formatted

        if (!product) {
          return
        }

        result.push({
          product_name: product.slug,
          start_date: storeCompanyProduct.started_at_formatted,
          end_date: endDate || null,
          price_ht: storeCompanyProduct.discounted_price_without_vat / 100,
          client: clientName,
          isSubscription: product.is_subscription,
          isSmsPack: smsPack !== null,
          smsIncluded: smsPack ? smsPack.included_quantity : null,
          stores: [
            {
              store_name: store?.name || '',
              store_id: store?.public_id || ''
            }
          ]
        })
      })
    }
  })
  return result
})

const subscriptions = computed(() => {
  return distributions.value.filter(
    (distribution) => distribution.isSubscription === true && distribution.isSmsPack === false
  )
})

const smsPacks = computed(() => {
  return distributions.value.filter((distribution) => distribution.isSmsPack === true)
})

const fetchData = () => {
  fetchClients().then(() => {
    fetchInvoices()
    isLoaded.value = true
  })
}

const fetchInvoices = (reset = true) => {
  if (reset) {
    isInvoicesLoaded.value = false
    invoices.value = []
    table.value.rows = []
    nextCursor.value = null
    hasMore.value = false
  } else {
    isLoadingMore.value = true
  }

  const paramsUrl: any = {}
  if (nextCursor.value) {
    paramsUrl.next_cursor = nextCursor.value
  }

  axios
    .get(`/invoices/list?client_id=${selectedClient.value}`, {
      params: paramsUrl
    })
    .then((response) => {
      const newInvoices = response.data.items || []

      if (reset) {
        invoices.value = newInvoices
      } else {
        invoices.value = [...invoices.value, ...newInvoices]
      }

      nextCursor.value = response.data.next_cursor || null
      hasMore.value = response.data.has_more || false

      const typeColor: Record<string, ChipColor> = {
        credit: 'green',
        setup: 'indigo',
        subscription: 'violet',
        sms: 'red',
        email: 'gray'
      }
      const statusColor: Record<string, ChipColor> = {
        archived: 'green',
        incomplete: 'indigo',
        cancelled: 'violet',
        paid: 'green',
        partially_cancelled: 'indigo',
        upcoming: 'gray',
        late: 'red',
        draft: 'green',
        credit_note: 'green'
      }

      const rows: TableRow[] = []
      invoices.value.forEach((row) => {
        const type = row.type || 'subscription'
        const date = row.date
        const status = row.status

        rows.push({
          id: row.id,
          data: {
            paymentReference: row.invoice_number,
            label: row.label || '',
            date: dayjs(row.date).format('DD/MM/YYYY'),
            paidMonth: {
              label: formatPaidMonth(dayjs(date).format('MMMM YYYY'))
            },
            totalPriceWithoutVat: String(formatNumber(row.currency_amount_before_tax, true, 2)),
            totalVat: String(formatNumber(row.currency_tax, true, 2)),
            totalPriceVatIncluded: String(formatNumber(row.currency_amount, true, 2)),
            type: {
              label: t(`pages.settings.accountPage.payments.types.${type}`),
              color: typeColor[type] || typeColor.subscription
            },
            status: {
              label: t(`pages.settings.accountPage.payments.statuses.${status}`),
              color: statusColor[status] || statusColor.draft
            },
            download:
              row.public_file_url !== null
                ? {
                    icon: 'HeroDocumentArrowDownIconSolid',
                    color: 'green'
                  }
                : {}
          }
        })
      })
      table.value.rows = rows
      isInvoicesLoaded.value = true
      isLoadingMore.value = false
    })
    .catch(() => {
      isInvoicesLoaded.value = true
      isLoadingMore.value = false
    })
}

const fetchClients = async () => {
  const response = await axios.get('/clients')
  clients.value = response.data.data
  if (clients.value.length > 0) {
    selectedClient.value = clients.value[0].public_id
  }
}

const formatPaidMonth = (paidMonth: string) => {
  return paidMonth.charAt(0).toUpperCase() + paidMonth.slice(1)
}

const updateMandate = (client: Client) => {
  axios
    .post(`/settings/gocardless/${client.public_id}/mandate`)
    .then((response) => {
      window.location.href = response.data.data.redirect_url
    })
    .catch((error) => {
      notification.create('error', {
        text: `Une erreur est survenue lors de la mise à jour du mandat de prélèvement. ${error.response.data.message}`,
        timeout: 5000
      })
    })
}

const updateDropdownValue = (index: number) => {
  const publicId = getClientPublicId(index)
  if (publicId) {
    selectedClient.value = publicId
    fetchInvoices(true)
  }
}

const download = (id: string) => {
  window.open(invoices.value.find((invoice) => invoice.id === id)?.public_file_url, '_blank')
}

const clientsDropdown = computed(() => {
  return clients.value.map((client, index) => ({
    id: index,
    text: client.name + (client.registration_number ? ` - ${client.registration_number}` : '')
  }))
})

const selectedClientIndex = computed(() => {
  return clientsDropdown.value.findIndex((c) => getClientPublicId(c.id) === selectedClient.value)
})

const getClientPublicId = (index: number): string | null => {
  return clients.value[index]?.public_id || null
}

const cancelSubscription = () => {
  const queryParams = `?user_id=${user.value.publicId}`
  openInNewTab(`https://unsubscribe.heypongo.com/${queryParams}`)
}

onMounted(() => {
  fetchData()
  useInfiniteScroll(
    window,
    () => {
      if (currentTab.value !== 'invoices') return
      if (hasMore.value && !isLoadingMore.value && isInvoicesLoaded.value) {
        fetchInvoices(false)
      }
    },
    { distance: 200 }
  )
})
</script>

<template>
  <div>
    <div v-if="isLoaded">
      <TheListHeader :title="t(pageTitle.title)">
        <template #actions>
          <Button v-if="currentTab === 'billing'" variant="outline" @click="cancelSubscription">
            <span class="text-foreground">
              {{ t(`pages.settings.subscriptionPage.cancelButton`) }}
            </span>
          </Button>
        </template>
      </TheListHeader>

      <div class="px-6 sm:px-12">
        <Tabs v-model="currentTab">
          <TabsList variant="underline">
            <TabsTrigger v-for="tab in tabs" :key="tab.id" variant="underline" :value="tab.id">
              <BaseIcon :name="tab.icon" class="h-4 w-4" aria-hidden="true" />
              {{ tab.name }}
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      <div v-if="currentTab === 'invoices'" class="p-6 sm:px-12 sm:pb-0">
        <div class="mb-6">
          <div v-if="clients.length > 1" class="w-full md:w-1/3">
            <ComboboxSelect
              value-key="id"
              label-key="text"
              placeholder="Entité facturée"
              trigger-class="w-full"
              :options="clientsDropdown"
              :model-value="selectedClientIndex"
              @update:model-value="updateDropdownValue"
            />
          </div>
          <p v-else-if="clients.length === 1" class="text-muted-foreground">
            {{ clients[0].name }} ({{ clients[0].registration_number }})
          </p>
          <p v-else class="text-muted-foreground">Aucune entité facturée disponible</p>
        </div>

        <div v-if="isInvoicesLoaded">
          <template v-if="table.rows?.length > 0">
            <AppTable :columns="translatedColumns" :rows="table.rows" :pagination="table.pagination" :sort="table.sort">
              <template #td-paidMonth="{ data }">
                <strong>{{ data.paidMonth.label }}</strong>
              </template>
              <template #td-type="{ data }">
                <BaseChip :color="data.type.color" :text="data.type.label" />
              </template>
              <template #td-status="{ data }">
                <BaseChip :color="data.status.color" :text="data.status.label" />
              </template>
              <template #td-download="{ id, data }">
                <Button
                  v-if="data.download.icon"
                  variant="ghost"
                  size="icon-sm"
                  :aria-label="t('pages.settings.accountPage.tables.thead.download')"
                  @click="download(id)"
                >
                  <Download />
                </Button>
                <span v-else class="text-muted-foreground">—</span>
              </template>
            </AppTable>
            <div class="flex justify-center py-4">
              <AppSkeleton v-if="isLoadingMore" type="list" />
            </div>
          </template>
          <TheEmptyState
            v-else
            module="settings.accountPage"
            :title="t(`pages.settings.accountPage.emptyState.title`)"
            :description="t(`pages.settings.accountPage.emptyState.description`)"
          />
        </div>
        <div v-else>
          <AppSkeleton type="list" />
        </div>
      </div>
      <div v-if="currentTab === 'billing'" class="p-6 sm:px-12 sm:pb-0">
        <h3 class="mb-6 text-lg font-semibold text-foreground">
          {{ t(`pages.settings.subscriptionPage.subscriptions.title`) }}
        </h3>
        <div v-if="subscriptions.length > 0" class="mt-6 grid grid-cols-6 gap-6">
          <SubscriptionItems :items="subscriptions" />
        </div>
        <div v-else class="mt-6 rounded-lg border border-border bg-muted p-8 text-center">
          <div class="mx-auto max-w-md">
            <svg class="mx-auto h-12 w-12 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
              />
            </svg>
            <h3 class="mt-4 text-sm font-medium text-foreground">
              {{ t('pages.settings.subscriptionPage.subscriptions.emptyState.title') }}
            </h3>
          </div>
        </div>

        <h3 class="mb-6 mt-10 text-lg font-semibold text-foreground">
          {{ t(`pages.settings.subscriptionPage.smsPacks.title`) }}
        </h3>
        <div v-if="smsPacks.length > 0" class="mt-6 grid grid-cols-6 gap-6">
          <SubscriptionItems :items="smsPacks" />
        </div>
        <div v-else class="mt-6 rounded-lg border border-border bg-muted p-8 text-center">
          <div class="mx-auto max-w-md">
            <svg class="mx-auto h-12 w-12 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
            <h3 class="mt-4 text-sm font-medium text-foreground">
              {{ t('pages.settings.subscriptionPage.smsPacks.emptyState.title') }}
            </h3>
          </div>
        </div>
      </div>
      <div v-if="currentTab === 'payment-methods'" class="p-6 sm:px-12 sm:pb-0">
        <div class="grid grid-cols-6 gap-6">
          <div
            v-for="client in clients"
            :key="client.public_id"
            class="group col-span-6 flex cursor-pointer flex-col justify-between rounded-lg border border-border transition-colors duration-200 hover:border-primary hover:shadow-[0_4px_14px_0_rgba(83,36,188,0.08)] md:col-span-3 lg:col-span-2"
          >
            <div class="flex flex-col justify-start">
              <div class="p-5">
                <div class="flex items-baseline justify-between">
                  <p class="mb-1 flex items-center text-base font-semibold text-foreground">
                    <span>{{ client.name ?? client.public_id }}</span>
                  </p>
                  <div class="mb-8 flex flex-row space-x-2">
                    <p class="rounded-md bg-primary/10 px-2 py-1.5 text-xs font-semibold leading-5 text-primary">
                      <span v-if="client.payment_method.slug === 'mandate'"
                        >{{
                          client.client_mandates.find((mandate) => mandate.isDefault === 1)?.mandate?.name ||
                          'Aucun mandat'
                        }}
                      </span>
                      <span v-else>{{ client.payment_method.name }}</span>
                    </p>
                  </div>
                </div>
                <div class="mb-2 line-clamp-4 text-sm uppercase text-foreground">
                  <div class="my-2 flex justify-between">
                    <p class="font-light">{{ t(`pages.settings.paymentMethodsPage.address`) }}</p>
                    <p class="font-medium">
                      {{ client.pennylane?.billing_address?.address }}
                      {{ client.pennylane?.billing_address?.postal_code }}
                      {{ client.pennylane?.billing_address?.city }}
                    </p>
                  </div>
                  <div class="flex justify-between">
                    <p class="font-light">{{ t(`pages.settings.paymentMethodsPage.siret`) }}</p>
                    <p class="font-medium">{{ client.registration_number }}</p>
                  </div>
                </div>
              </div>
            </div>
            <div
              v-if="client.payment_method.slug === 'mandate'"
              class="flex flex-row justify-end border-t p-3 border-t-border text-primary"
            >
              <BaseConfirmButton
                :title="client.name"
                text="Vous vous apprêtez à modifier votre mandat de prélèvement. Cette page permet uniquement de changer vos coordonnées bancaires. Pour toute autre modification, veuillez contacter le support."
                :confirm-button-text="t(`pages.settings.paymentMethodsPage.updateMandate`)"
                confirm-button-label="Mettre à jour"
                :cancel-button-label="t(`ui.confirmModal.buttons.cancel`)"
                :white="false"
                @confirm="updateMandate(client)"
              >
                {{ t(`pages.settings.paymentMethodsPage.updateMandate`) }}
              </BaseConfirmButton>
            </div>
          </div>
        </div>
      </div>
    </div>
    <TheLoadingPage v-else />
  </div>
</template>
